import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import ArticleFolderListData from './articleFolderListData';
import { useNavigate } from 'react-router-dom';
import { PersistedFixedSizeList as FixedSizeList } from "../../hooks/listNavigation";
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
    title: 500, description: 800
};

const ArticleFolderList = React.memo(({ scrollRef, articleFolders, sortBy, setSortBy, setOffset, filteredArticleFoldersCount, setArticleFoldersFetched, canReorder = false, onMove }) => {

    const navigate = useNavigate();

    const [articleFolderColumnWidth, setArticleFolderColumnWidth] = useState(() => {
        const storedWidths = JSON.parse(localStorage.getItem('articleFolderColumnWidth')) || {};
        return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
    });

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    const tableHeadRef = useRef(null);

    const handleColumnWidthChange = (value, columnKey) => {
        setArticleFolderColumnWidth({ ...articleFolderColumnWidth, [columnKey]: value })
    }

    useEffect(() => {
        localStorage.setItem('articleFolderColumnWidth', JSON.stringify(articleFolderColumnWidth));
    }, [articleFolderColumnWidth]);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(filteredArticleFoldersCount, setArticleFoldersFetched, 70, 280);

    // Handle scroll and fetch more items
    const handleScrollList = useScrollFetchData(setOffset, setArticleFoldersFetched, filteredArticleFoldersCount, 70, 280);

    const handleArticleFolderClick = useCallback((articleFolderId) => navigate(`dossiers_articles_details/${articleFolderId}`), [navigate]);

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };

    const Row = ({ index, style, data }) => {
        const { articleFolders, handleArticleFolderClick, canReorder, onMove, itemCount } = data;
        const articleFolder = articleFolders[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <ArticleFolderListData
                    articleFolder={articleFolder}
                    columnWidth={articleFolderColumnWidth}
                    handleArticleFolderClick={handleArticleFolderClick}
                    canReorder={canReorder}
                    disableUp={index <= 0}
                    disableDown={index >= itemCount - 1}
                    onMove={onMove}
                />
            </Box>
        );
    };

    return (
        <Box className='table'>
            <Box className='tableHead' ref={tableHeadRef}>
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label='Titre'
                    columnKey='title'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    isFixed={true}
                    columnWidth={articleFolderColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label='Description'
                    columnKey='description'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    columnWidth={articleFolderColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter' width="130px" />
            </Box>
            <Box className='tableBody'>
                <FixedSizeList
                    ref={scrollRef}
                    height={Math.min(
                        filteredArticleFoldersCount * 70 + 15,
                        windowHeight - 285
                    )}
                    width='100%'
                    itemCount={filteredArticleFoldersCount}
                    itemSize={70}
                    itemData={{ articleFolders, handleArticleFolderClick, canReorder, onMove, itemCount: filteredArticleFoldersCount }}
                    onScroll={handleScrollList}
                >
                    {Row}
                </FixedSizeList>
            </Box>
        </Box>

    );
});

export default ArticleFolderList;